import React, { useState, useRef, useEffect } from 'react';
import { Task, CreateTaskInput } from '../../services/taskService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format, addDays, isToday } from 'date-fns';
import EasyMDE from 'easymde';
import 'easymde/dist/easymde.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '@/styles/easymde.css';
import { supabase } from '@/lib/supabaseClient';
import { SearchableSelect } from '../ui/searchable-select';
import { ProjectSelect } from '../ui/project-select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { FormField } from '../ui/form-field';
import { getCurrentTenantId } from '@/utils/tenantUtils';

interface TaskFormProps {
  task?: Task;
  onSubmit: (task: CreateTaskInput) => Promise<void>;
  onCancel: () => void;
  inDialog?: boolean;
  isSubmitting?: boolean;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

interface ProjectOption {
  id: string;
  name: string;
  company: string;
}

interface TeamMember {
  user_id: string;
  users: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
  } | null;
}

interface UserWithRole {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  role_id: string | null;
  roles: {
    name: string;
  } | null;
}

export const TaskForm: React.FC<TaskFormProps> = ({
  task,
  onSubmit,
  onCancel,
  inDialog = false,
  isSubmitting: externalIsSubmitting = false,
}) => {
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [priority, setPriority] = useState<string>(task?.priority || 'medium');
  const [dueDate, setDueDate] = useState<Date | undefined>(
    task?.due_date ? new Date(task.due_date) : undefined
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editorRef = useRef<EasyMDE | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [assigneeId, setAssigneeId] = useState<string>(task?.assignee_id ?? '');
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [currentUser, setCurrentUser] = useState<{ id: string; email: string } | null>(null);
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
  const [projectId, setProjectId] = useState<string>(task?.project_id ?? '');
  const [projectOptions, setProjectOptions] = useState<{ id: string; project_name: string; company_name: string }[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [hoveredDate, setHoveredDate] = useState<Date | undefined>(undefined);
  const [taskType, setTaskType] = useState<'personal' | 'project'>(
    task?.project_id ? 'project' : 'project'
  );
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [specificProjectLoading, setSpecificProjectLoading] = useState(false);

  const modifiers: any = {
    today: (date: Date) => isToday(date),
  };
  if (hoveredDate) {
    modifiers.hovered = hoveredDate;
  }

  useEffect(() => {
    let easyMDE: EasyMDE | null = null;

    const initializeEditor = () => {
      if (!textareaRef.current) return;

      try {
        easyMDE = new EasyMDE({
          element: textareaRef.current,
          spellChecker: false,
          status: false,
          theme: 'easymde',
          autofocus: false,
          placeholder: 'Task description (optional)',
          minHeight: '150px',
          maxHeight: '400px',
          autoDownloadFontAwesome: true,
          previewRender: (text) => {
            return text;
          },
          toolbar: [
            {
              name: "bold",
              action: EasyMDE.toggleBold,
              className: "fa fa-bold",
              title: "Bold",
            },
            {
              name: "italic",
              action: EasyMDE.toggleItalic,
              className: "fa fa-italic",
              title: "Italic",
            },
            {
              name: "heading",
              action: EasyMDE.toggleHeadingSmaller,
              className: "fa fa-header",
              title: "Heading",
            },
            "|",
            {
              name: "quote",
              action: EasyMDE.toggleBlockquote,
              className: "fa fa-quote-left",
              title: "Quote",
            },
            {
              name: "unordered-list",
              action: EasyMDE.toggleUnorderedList,
              className: "fa fa-list-ul",
              title: "Unordered List",
            },
            {
              name: "ordered-list",
              action: EasyMDE.toggleOrderedList,
              className: "fa fa-list-ol",
              title: "Ordered List",
            },
            "|",
            {
              name: "link",
              action: EasyMDE.drawLink,
              className: "fa fa-link",
              title: "Insert Link",
            },
            {
              name: "image",
              action: EasyMDE.drawImage,
              className: "fa fa-picture-o",
              title: "Insert Image",
            },
            "|",
            {
              name: "preview",
              action: EasyMDE.togglePreview,
              className: "fa fa-eye no-disable",
              title: "Toggle Preview",
            },
            {
              name: "side-by-side",
              action: EasyMDE.toggleSideBySide,
              className: "fa fa-columns no-disable no-mobile",
              title: "Toggle Side by Side",
            },
            {
              name: "fullscreen",
              action: EasyMDE.toggleFullScreen,
              className: "fa fa-arrows-alt no-disable no-mobile",
              title: "Toggle Fullscreen",
            },
            "|",
            {
              name: "guide",
              action: "https://www.markdownguide.org/basic-syntax/",
              className: "fa fa-question-circle",
              title: "Markdown Guide",
            },
          ],
          toolbarButtonClassPrefix: "easymde-toolbar-button",
        });

        editorRef.current = easyMDE;

        // Set initial value if editing an existing task
        if (description) {
          easyMDE.value(description);
        }

        easyMDE.codemirror.on('change', () => {
          setDescription(easyMDE?.value() || '');
        });

        // Add click handler to focus the editor and show cursor
        if (easyMDE) {
          const wrapper = easyMDE.codemirror.getWrapperElement();
          if (wrapper) {
            wrapper.style.cursor = 'text';
            wrapper.addEventListener('click', (e) => {
              e.stopPropagation();
              if (easyMDE) {
                easyMDE.codemirror.focus();
                // Force cursor to show
                setTimeout(() => {
                  if (easyMDE) {
                    easyMDE.codemirror.setCursor(easyMDE.codemirror.getCursor());
                    easyMDE.codemirror.refresh();
                  }
                }, 10);
              }
            });
          }

          // Set cursor visibility options
          easyMDE.codemirror.setOption('cursorBlinkRate', 530);
          easyMDE.codemirror.setOption('showCursorWhenSelecting', true);
          easyMDE.codemirror.setOption('cursorHeight', 1);
          
          // Ensure cursor is visible on first focus
          easyMDE.codemirror.on('focus', () => {
            setTimeout(() => {
              if (easyMDE) {
                const cursor = easyMDE.codemirror.getCursor();
                easyMDE.codemirror.setCursor(cursor);
              }
            }, 0);
          });
        }
      } catch (error) {
        console.error('Error initializing EasyMDE:', error);
      }
    };

    // Initialize editor after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(initializeEditor, 0);

    return () => {
      clearTimeout(timeoutId);
      if (easyMDE) {
        try {
          easyMDE.toTextArea();
          easyMDE.cleanup();
        } catch (error) {
          console.error('Error cleaning up EasyMDE:', error);
        }
      }
    };
  }, []);

  useEffect(() => {
    async function getCurrentUser() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUser({ id: user.id, email: user.email || '' });
        // Only set assigneeId to current user if no task is being edited (new task)
        if (!task?.id) {
          setAssigneeId(user.id);
        }

        // Fetch current user's profile to get their name
        let currentUserName = 'Myself';
        try {
          const { data: userProfile, error } = await supabase
            .from('users')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single();

          if (!error && userProfile) {
            const fullName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
            if (fullName) {
              currentUserName = `Myself (${fullName})`;
            }
          }
        } catch (error) {
          console.error('Error fetching current user profile:', error);
        }

        // Initialize userOptions with current user
        const initialOptions = [{ id: user.id, name: currentUserName, email: user.email || '' }];
        
        // If editing a task and assignee is different from current user, fetch and add them
        if (task?.assignee_id && task.assignee_id !== user.id) {
          try {
            const { data: assigneeData, error } = await supabase
              .from('users')
              .select('id, first_name, last_name, email')
              .eq('id', task.assignee_id)
              .single();
              
            if (!error && assigneeData) {
              const assigneeName = `${assigneeData.first_name || ''} ${assigneeData.last_name || ''}`.trim() || assigneeData.email;
              initialOptions.push({
                id: assigneeData.id,
                name: assigneeName,
                email: assigneeData.email
              });
            }
          } catch (error) {
            console.error('Error fetching assignee data:', error);
          }
        }
        
        setUserOptions(initialOptions);
      }
    }
    getCurrentUser();
    // eslint-disable-next-line
  }, []);

  const fetchAssignableUsers = async () => {
    if (usersLoaded || !currentUser) return;

    try {
      // Fetch all users, excluding deleted ones
      const { data: usersWithRoles, error } = await supabase
        .from('users')
        .select(`
          id,
          first_name,
          last_name,
          email,
          role_id,
          deleted_at,
          roles (
            name
          )
        `)
        .is('deleted_at', null);

      if (error) {
        console.error('Error fetching users:', error);
        return;
      }

      // Map all non-deleted users
      const allUsers = (usersWithRoles || [])
        .map(user => ({
          id: user.id,
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
          email: user.email,
        }));

      // Always include self as 'Myself' first
      // Fetch current user's profile to get their name for the self option
      let currentUserName = 'Myself';
      try {
        const { data: userProfile, error } = await supabase
          .from('users')
          .select('first_name, last_name')
          .eq('id', currentUser.id)
          .single();

        if (!error && userProfile) {
          const fullName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
          if (fullName) {
            currentUserName = `Myself (${fullName})`;
          }
        }
      } catch (error) {
        console.error('Error fetching current user profile:', error);
      }

      const selfOption: UserOption = {
        id: currentUser.id,
        name: currentUserName,
        email: currentUser.email,
      };
      
      // Remove duplicate if current user is in the list
      const filteredUsers = allUsers.filter(u => u.id !== currentUser.id);
      
      // Add 'Myself' at the beginning
      const finalUsers = [selfOption, ...filteredUsers];

      setUserOptions(finalUsers);
      setUsersLoaded(true);
    } catch (error) {
      console.error('Error in fetchAssignableUsers:', error);
      // Still set 'Myself' as an option even if the query fails
      // Try to get user's name for fallback option too
      let fallbackUserName = 'Myself';
      try {
        const { data: userProfile, error } = await supabase
          .from('users')
          .select('first_name, last_name')
          .eq('id', currentUser.id)
          .single();

        if (!error && userProfile) {
          const fullName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
          if (fullName) {
            fallbackUserName = `Myself (${fullName})`;
          }
        }
      } catch (profileError) {
        console.error('Error fetching user profile in fallback:', profileError);
      }

      setUserOptions([{ id: currentUser.id, name: fallbackUserName, email: currentUser.email }]);
      setUsersLoaded(true);
    }
  };

  const fetchProjects = async (searchTerm: string = '') => {
    if (projectsLoaded && !searchTerm) return;
    if (projectsLoading) return; // Prevent multiple simultaneous requests

    setProjectsLoading(true);
    try {
      // Get current tenant ID to filter projects
      const tenantId = await getCurrentTenantId();
      if (!tenantId) {
        console.error('Could not get tenant ID');
        return;
      }
      

      const query = supabase
        .from('projects')
        .select('id, project_name, company_name')
        .in('project_status', ['active', 'Active']) // Handle both cases
        .eq('tenant_id', tenantId) // Only load projects from current tenant
        .order('created_at', { ascending: false })
        .limit(10); // Show latest 10 projects for faster loading

      if (searchTerm) {
        query.or(`project_name.ilike.%${searchTerm}%,company_name.ilike.%${searchTerm}%`);
        query.limit(50); // Even fewer results when searching
      }

      const { data: projects, error } = await query;

      if (error) throw error;


      // Preserve any existing projects that aren't in the new results (like pre-selected project)
      setProjectOptions(prevOptions => {
        const newProjects = projects || [];
        const existingIds = new Set(newProjects.map(p => p.id));
        const preservedProjects = prevOptions.filter(p => !existingIds.has(p.id));


        // Put preserved projects first, then new projects
        return [...preservedProjects, ...newProjects];
      });

      if (!searchTerm) {
        setProjectsLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!title.trim()) {
      alert('Please enter a task title');
      return;
    }

    if (!assigneeId) {
      alert('Please select an assignee');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        priority: priority as 'low' | 'medium' | 'high',
        due_date: dueDate?.toISOString(),
        assignee_id: assigneeId,
        project_id: (taskType === 'project' || task?.project_id) ? (projectId || undefined) : undefined,
      });
    } catch (error) {
      console.error('Error submitting task:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch the specific project when pre-selected
  const fetchSpecificProject = async (projectId: string): Promise<void> => {
    if (specificProjectLoading) return;

    setSpecificProjectLoading(true);
    try {
      const { data: project, error } = await supabase
        .from('projects')
        .select('id, project_name, company_name')
        .eq('id', projectId)
        .single();

      if (!error && project) {
        // Add this project to the options if it's not already there
        setProjectOptions(prevOptions => {
          const exists = prevOptions.some(p => p.id === project.id);
          if (!exists) {
            return [project, ...prevOptions];
          }
          return prevOptions;
        });
      }
    } catch (error) {
      console.error('Error fetching specific project:', error);
    } finally {
      setSpecificProjectLoading(false);
    }
  };

  useEffect(() => {
    if (task?.project_id) {
      // Load the specific project first, then fetch general projects list
      fetchSpecificProject(task.project_id).then(() => {
        fetchProjects();
      });
    }
  }, [task?.project_id]);


  // If switching to personal, clear projectId
  useEffect(() => {
    if (taskType === 'personal') {
      setProjectId('');
    }
  }, [taskType]);

  // Load projects when project dropdown opens (but not when task has project_id - that's handled above)
  useEffect(() => {
    if (projectDropdownOpen && !projectsLoaded && !task?.project_id) {
      fetchProjects();
    }
  }, [projectDropdownOpen, projectsLoaded, task?.project_id]);

  // Load users when assignee dropdown opens
  useEffect(() => {
    if (assigneeDropdownOpen && !usersLoaded && currentUser) {
      fetchAssignableUsers();
    }
  }, [assigneeDropdownOpen, usersLoaded, currentUser]);

  // Use external isSubmitting if provided, otherwise use internal state
  const submittingState = externalIsSubmitting || isSubmitting;

  return (
    <div className={inDialog ? "" : "p-4 relative isolate"}>
      <form
        onSubmit={handleSubmit}
        className="space-y-4 sm:space-y-6"
        noValidate
      >
        {/* Task Type Toggle - Only show if no project pre-selected */}
        {!task?.project_id && (
          <div className="mb-2">
            <Tabs value={taskType} onValueChange={v => setTaskType(v as 'personal' | 'project')} className="w-full">
              <TabsList className="w-full flex rounded-md bg-muted p-1">
                <TabsTrigger
                  value="project"
                  className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-muted/50 rounded-md transition-colors"
                >
                  Project Task
                </TabsTrigger>
                <TabsTrigger
                  value="personal"
                  className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-muted/50 rounded-md transition-colors"
                >
                  Personal Task
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}
        {/* Title */}
        <div className="space-y-2">
          <label htmlFor="title" className="text-sm font-medium text-foreground">
            Title
          </label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter task title"
            required
          />
        </div>
        {/* Project Dropdown (only for Project Task or when project pre-selected) */}
        {(taskType === 'project' || task?.project_id) && (
          <FormField label="Project" htmlFor="project">
            <ProjectSelect
              options={projectOptions}
              value={projectId}
              onValueChange={setProjectId}
              placeholder="Search for a project..."
              className="w-full"
              onOpenChange={setProjectDropdownOpen}
              loading={projectsLoading}
              onSearchChange={fetchProjects}
            />
          </FormField>
        )}
        <FormField label="Assignee" htmlFor="assignee_id" required>
          <SearchableSelect
            id="assignee_id"
            value={assigneeId}
            onValueChange={setAssigneeId}
            options={userOptions.map(u => ({
              label: u.name.startsWith('Myself') ? u.name : `${u.name} (${u.email})`,
              value: u.id
            }))}
            placeholder="Assign to..."
            onOpenChange={setAssigneeDropdownOpen}
            inDialog={inDialog}
          />
        </FormField>

        <div className="border rounded-md overflow-hidden EasyMDEContainer">
          <textarea ref={textareaRef} />
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <FormField label="Priority" htmlFor="priority">
              <SearchableSelect
                id="priority"
                value={priority}
                onValueChange={setPriority}
                options={[
                  { label: "Low", value: "low" },
                  { label: "Medium", value: "medium" },
                  { label: "High", value: "high" }
                ]}
                placeholder="Select priority"
                inDialog={inDialog}
              />
            </FormField>
          </div>

          <div className="flex-1">
            <FormField label="Due Date" htmlFor="due-date">
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, 'PPP') : 'Set due date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={(date) => {
                    setDueDate(date);
                    setCalendarOpen(false);
                  }}
                  initialFocus
                  modifiers={modifiers}
                  modifiersClassNames={{
                    hovered: 'bg-primary/10 text-yellow-900',
                    selected: 'bg-primary text-primary-foreground',
                    today: 'bg-muted text-muted-foreground',
                  }}
                  onDayMouseEnter={setHoveredDate}
                  onDayMouseLeave={() => setHoveredDate(undefined)}
                />
              </PopoverContent>
              </Popover>
            </FormField>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={submittingState}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submittingState}
            className="w-full sm:w-auto"
          >
            {submittingState ? 'Saving...' : task?.id ? 'Update Task' : 'Create Task'}
          </Button>
        </div>
      </form>
    </div>
  );
}; 